// src/firebase/auth.js

import { auth, firestore } from "./config";
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  updatePassword as firebaseUpdatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
} from "firebase/auth";
import { doc, getDoc, setDoc } from "firebase/firestore";

// ── Roles: "admin" | "trainer" | "student" ────────────────────────────────

export async function loginUser(email, password) {
  const cred = await signInWithEmailAndPassword(auth, email, password);
  const userData = await getUserData(cred.user.uid);
  return { user: cred.user, role: userData?.role || "student" };
}

export async function logoutUser() {
  await signOut(auth);
}

export async function getUserData(uid) {
  const snap = await getDoc(doc(firestore, "users", uid));
  return snap.exists() ? snap.data() : { role: "student" };
}

/** Create an admin, trainer, or student account */
export async function createStaffAccount(email, password, name, role, extraData = {}) {
  const cred = await createUserWithEmailAndPassword(auth, email, password);
  await setDoc(doc(firestore, "users", cred.user.uid), { 
    email, name, role, ...extraData, createdAt: new Date().toISOString() 
  });
  return cred.user;
}

/** Link a student to a Firebase Auth account (for diary login) */
export async function createStudentAccount(email, password, studentId, name) {
  const cred = await createUserWithEmailAndPassword(auth, email, password);
  await setDoc(doc(firestore, "users", cred.user.uid), {
    email, name, role: "student", studentId, createdAt: new Date().toISOString(),
  });
  return cred.user;
}

export function onAuthChange(callback) {
  return onAuthStateChanged(auth, async user => {
    if (!user) return callback(null, null);
    const userData = await getUserData(user.uid);
    callback(user, userData);
  });
}

export async function resetPassword(email) {
  await sendPasswordResetEmail(auth, email);
}

export async function updatePassword(newPassword) {
  if (!auth.currentUser) throw new Error("No user logged in");
  await firebaseUpdatePassword(auth.currentUser, newPassword);
}

export async function changePasswordSecurely(currentPassword, newPassword) {
  const user = auth.currentUser;
  if (!user) throw new Error("No user logged in");
  
  const credential = EmailAuthProvider.credential(user.email, currentPassword);
  await reauthenticateWithCredential(user, credential);
  await firebaseUpdatePassword(user, newPassword);
}
